/**
 * Admin Question Review Queue — S5.1
 *
 *   /admin/sorular
 *
 * DRAFT soruları listele, Claude ile yeni üret, approve/reject.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface QuestionRow {
  id: string;
  code: string;
  topic: string;
  subtopic: string;
  difficulty: string;
  format: string;
  questionText: string;
  imageUrl: string | null;
  options: Array<{ key: string; text: string; correct?: boolean }>;
  correctAnswer: string | null;
  explanation: string;
  commonMistakes: string[];
  points: number;
  estimatedTimeSec: number;
  reviewStatus: string;
  generatedByAI: boolean;
  generationPromptVersion: string | null;
  createdAt: string;
}

const TOPICS = ['PM', 'AS', 'DR', 'SK', 'SM', 'WD', 'SF', 'CF', 'GN', 'DT'];
const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel',
  DT: 'Teori',
};
const DIFFICULTIES = ['L1', 'L2', 'L3'];
const FORMATS = ['MC', 'MC_NEG', 'IMG', 'MASS', 'COM'];

export function AdminQuestionsPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [statusFilter, setStatusFilter] = useState<'DRAFT' | 'APPROVED' | 'ARCHIVED'>(
    'DRAFT',
  );
  const [topicFilter, setTopicFilter] = useState<string>('');
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      if (topicFilter) params.set('topic', topicFilter);
      const result = await api.get<{ questions: QuestionRow[] }>(
        `/api/admin/questions?${params.toString()}`,
      );
      setQuestions(result.questions);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Sorular yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, topicFilter, session, sessionPending]);

  async function approve(id: string): Promise<void> {
    try {
      await api.patch(`/api/admin/questions/${id}/approve`);
      setQuestions((q) => q.filter((x) => x.id !== id));
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function reject(id: string): Promise<void> {
    if (!confirm('Bu soruyu arşivle?')) return;
    try {
      await api.patch(`/api/admin/questions/${id}/reject`);
      setQuestions((q) => q.filter((x) => x.id !== id));
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function remove(id: string, code: string): Promise<void> {
    if (
      !confirm(
        `"${code}" sorusunu KALICI olarak sil?\n\nGeri alınamaz. Aktif sınavda kullanılıyorsa engellenir.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/admin/questions/${id}`);
      setQuestions((q) => q.filter((x) => x.id !== id));
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Silme başarısız');
    }
  }

  const [editingId, setEditingId] = useState<string | null>(null);
  const editingQ = editingId ? questions.find((q) => q.id === editingId) ?? null : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => setLocation('/admin/dashboard')}
              className="text-sm font-medium text-blue-900 hover:underline"
            >
              ← Admin Panel
            </button>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Soru Havuzu Yönetimi
            </h1>
            <p className="text-sm text-slate-600">
              AI üretilmiş taslakları incele, onayla veya arşivle.
            </p>
          </div>
          <Button onClick={() => setShowGenerate(true)}>+ Yeni Üret</Button>
        </div>

        {error && (
          <div className="mb-4">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {showGenerate && (
          <GenerateModal
            onClose={() => setShowGenerate(false)}
            onCreated={async () => {
              setShowGenerate(false);
              await refresh();
            }}
          />
        )}

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">
                Durum:
              </span>
              {(['DRAFT', 'APPROVED', 'ARCHIVED'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    statusFilter === s
                      ? 'bg-blue-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">
                Konu:
              </span>
              <select
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="h-8 rounded-lg border border-slate-300 px-2 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="">Tümü</option>
                {TOPICS.map((t) => (
                  <option key={t} value={t}>
                    {TOPIC_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {loading ? (
          <p className="text-slate-500">Yükleniyor…</p>
        ) : questions.length === 0 ? (
          <Card>
            <p className="text-center text-slate-500">
              {statusFilter} durumunda soru yok.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {questions.map((q) => (
              <QuestionCard
                key={q.id}
                q={q}
                onApprove={() => approve(q.id)}
                onReject={() => reject(q.id)}
                onEdit={() => setEditingId(q.id)}
                onDelete={() => remove(q.id, q.code)}
              />
            ))}
          </div>
        )}

        {editingQ && (
          <EditModal
            q={editingQ}
            onClose={() => setEditingId(null)}
            onSaved={(updated) => {
              setQuestions((qs) =>
                qs.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)),
              );
              setEditingId(null);
            }}
          />
        )}
      </main>
    </div>
  );
}

function QuestionCard({
  q,
  onApprove,
  onReject,
  onEdit,
  onDelete,
}: {
  q: QuestionRow;
  onApprove: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
  onEdit: () => void;
  onDelete: () => void | Promise<void>;
}): JSX.Element {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-slate-700">
            {q.code}
          </code>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-900">
            {q.topic} · {q.subtopic}
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
            {q.difficulty}
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
            {q.format}
          </span>
          {q.generatedByAI && (
            <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">
              AI ({q.generationPromptVersion ?? '?'})
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Düzenle
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            Sil
          </button>
          {q.reviewStatus === 'DRAFT' && (
            <>
              <button
                type="button"
                onClick={onReject}
                className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
              >
                Arşivle
              </button>
              <button
                type="button"
                onClick={onApprove}
                className="rounded-lg bg-green-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-800"
              >
                ✓ Onayla
              </button>
            </>
          )}
        </div>
      </div>

      <p className="mt-3 text-base font-medium text-slate-900">
        {q.questionText}
      </p>

      {q.imageUrl && (
        <img
          src={q.imageUrl}
          alt="Soru görseli"
          className="mt-3 max-h-60 rounded-lg border border-slate-200"
        />
      )}

      {q.options.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {q.options.map((o) => {
            const isCorrect =
              o.correct === true || o.key === q.correctAnswer;
            return (
              <li
                key={o.key}
                className={`flex items-start gap-2 rounded p-1.5 ${
                  isCorrect ? 'bg-green-50' : ''
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    isCorrect
                      ? 'bg-green-700 text-white'
                      : 'border border-slate-300 text-slate-600'
                  }`}
                >
                  {o.key}
                </span>
                <span className="text-slate-800">{o.text}</span>
                {isCorrect && (
                  <span className="ml-auto text-xs font-medium text-green-700">
                    ✓ Doğru
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
        <p className="font-medium text-slate-700">Açıklama:</p>
        <p className="text-slate-600">{q.explanation}</p>
      </div>

      {q.commonMistakes.length > 0 && (
        <div className="mt-2 text-sm">
          <p className="font-medium text-slate-700">Yaygın Hatalar:</p>
          <ul className="ml-4 list-disc text-slate-600">
            {q.commonMistakes.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
        <span>{q.points} puan</span>
        <span>~{q.estimatedTimeSec}s</span>
        <span>Oluşturuldu: {new Date(q.createdAt).toLocaleDateString('tr-TR')}</span>
      </div>
    </Card>
  );
}

function GenerateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [topic, setTopic] = useState('PM');
  const [subtopic, setSubtopic] = useState('feature_tree');
  const [difficulty, setDifficulty] = useState('L1');
  const [format, setFormat] = useState('MC');
  const [count, setCount] = useState(3);
  const [mode, setMode] = useState<'preview' | 'final'>('preview');
  const [contextNotes, setContextNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function generate(): Promise<void> {
    setGenerating(true);
    setError(null);
    setInfo(null);
    try {
      const result = await api.post<{
        created: { length: number };
        modelUsed: string;
        costUsd: number;
      }>('/api/admin/questions/generate', {
        topic,
        subtopic,
        difficulty,
        format,
        count,
        mode,
        contextNotes: contextNotes || undefined,
      });
      setInfo(
        `${count} taslak üretildi (${result.modelUsed}, $${result.costUsd.toFixed(4)}). DRAFT durumunda.`,
      );
      await onCreated();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Üretim başarısız');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg">
        <h3 className="text-lg font-semibold text-slate-900">Soru Üret</h3>
        <p className="mt-1 text-sm text-slate-600">
          Gateway (mock veya real) üzerinden AI ile taslak oluşturur.
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}
        {info && !error && (
          <div className="mt-3">
            <Alert variant="success">{info}</Alert>
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Konu" htmlFor="g-topic">
            <select
              id="g-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TOPICS.map((t) => (
                <option key={t} value={t}>
                  {TOPIC_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Alt Konu" htmlFor="g-subtopic">
            <Input
              id="g-subtopic"
              value={subtopic}
              onChange={(e) => setSubtopic(e.target.value)}
            />
          </Field>
          <Field label="Zorluk" htmlFor="g-diff">
            <select
              id="g-diff"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Format" htmlFor="g-fmt">
            <select
              id="g-fmt"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Adet" htmlFor="g-count">
            <Input
              id="g-count"
              type="number"
              min={1}
              max={10}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
            />
          </Field>
          <Field label="Mod" htmlFor="g-mode" hint="preview=Haiku, final=Opus">
            <select
              id="g-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'preview' | 'final')}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="preview">Preview (ucuz)</option>
              <option value="final">Final (pahalı, kalite)</option>
            </select>
          </Field>
        </div>

        <Field
          label="Context Notes (opsiyonel)"
          htmlFor="g-ctx"
          hint="Claude'a ek bağlam — örn. 'CSWA müfredatı', 'Spec 5.4 referans alınsın'"
        >
          <textarea
            id="g-ctx"
            value={contextNotes}
            onChange={(e) => setContextNotes(e.target.value)}
            maxLength={500}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </Field>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={generating}>
            İptal
          </Button>
          <Button onClick={generate} loading={generating}>
            Üret
          </Button>
        </div>
      </Card>
    </div>
  );
}

function EditModal({
  q,
  onClose,
  onSaved,
}: {
  q: QuestionRow;
  onClose: () => void;
  onSaved: (updated: QuestionRow) => void;
}): JSX.Element {
  const [questionText, setQuestionText] = useState(q.questionText);
  const [options, setOptions] = useState(
    q.options.map((o) => ({ key: o.key, text: o.text })),
  );
  const [correctAnswer, setCorrectAnswer] = useState(q.correctAnswer ?? '');
  const [explanation, setExplanation] = useState(q.explanation);
  const [points, setPoints] = useState(q.points);
  const [estimatedTimeSec, setEstimatedTimeSec] = useState(q.estimatedTimeSec);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const optionsWithCorrect = options.map((o) => ({
        key: o.key,
        text: o.text,
        correct: o.key === correctAnswer,
      }));
      const updated = await api.patch<QuestionRow>(
        `/api/admin/questions/${q.id}`,
        {
          questionText,
          options: optionsWithCorrect,
          correctAnswer,
          explanation,
          points,
          estimatedTimeSec,
        },
      );
      onSaved(updated);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  }

  function updateOption(idx: number, text: string): void {
    setOptions((opts) => opts.map((o, i) => (i === idx ? { ...o, text } : o)));
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            Soruyu Düzenle
          </h3>
          <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
            {q.code}
          </code>
        </div>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <Field label="Soru Metni" htmlFor="e-text">
          <textarea
            id="e-text"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </Field>

        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-slate-700">Seçenekler</p>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={o.key} className="flex items-start gap-2">
                <label className="flex items-center gap-1 pt-2">
                  <input
                    type="radio"
                    name="correctAnswer"
                    value={o.key}
                    checked={correctAnswer === o.key}
                    onChange={() => setCorrectAnswer(o.key)}
                    className="h-4 w-4"
                  />
                  <span className="font-mono text-sm text-slate-700">
                    {o.key}
                  </span>
                </label>
                <textarea
                  value={o.text}
                  onChange={(e) => updateOption(i, e.target.value)}
                  rows={2}
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Doğru cevabın anahtarını (a/b/c/d) sol radyo butonundan seç.
          </p>
        </div>

        <Field label="Açıklama" htmlFor="e-exp">
          <textarea
            id="e-exp"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </Field>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Puan" htmlFor="e-pts">
            <Input
              id="e-pts"
              type="number"
              min={1}
              max={50}
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
            />
          </Field>
          <Field label="Tahmini Süre (sn)" htmlFor="e-time">
            <Input
              id="e-time"
              type="number"
              min={15}
              max={600}
              value={estimatedTimeSec}
              onChange={(e) => setEstimatedTimeSec(Number(e.target.value))}
            />
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            İptal
          </Button>
          <Button onClick={save} loading={saving}>
            Kaydet
          </Button>
        </div>
      </Card>
    </div>
  );
}
